import * as strings from "common/strings";
import * as config from "config";
import * as cession from "domain/cession";
import * as programActionTypes from "store/actions/input/program/programActionTypes";
import * as persistenceActionTypes from "store/actions/persistence/persistenceActionTypes";
import * as searchActionTypes from "store/actions/search/searchActionTypes";

// NOTE:
// Avoid adding defaults here, they should go in the resolver if required

export const INIT_STATE = {
  insured: "",
  riskDescription: "",
  inception: null,
  expiration: null,
  proRataAdjustment: null,
  reference: "",
  naics: null,
  inUS: true,
  turnoverCategory: "E",
  industryClass: null,
  brokerage: null,
  otherAcquisitionCosts: null,
  profit: null,
  expenses: null,
  policyFee: null,
  inspectionFee: null,
  duns: [],
  policyForms: [],
  insuredAddress: "",
  mailingAddress: "",
  insuredAddressComponents: null,
  currency: "USD",
  reassured: null,
  paymentTerms: null,
  notes: null,
  riskSummary: null,
  model: null,
  productLine: null,
  reinsured: null,
  mga: null,
  cessionLimitFrom: null,
  cessionLimitTo: null,
};

const getKey = (action) => {
  switch (action.type) {
    case programActionTypes.ADD_DUNS:
    case programActionTypes.REMOVE_DUNS:
      return "duns";
    default:
      return null;
  }
};

const parseFloatOrNull = (input) => (input != null ? parseFloat(input) : null);

const programReducer = (state, action) => {
  if (state === undefined) {
    state = {
      ...INIT_STATE,
    };
  }
  const key = getKey(action);
  switch (action.type) {
    case persistenceActionTypes.LOAD_SUBMISSION_STARTED:
      return {
        ...INIT_STATE,
      };
    case persistenceActionTypes.LOAD_SUBMISSION_SUCCEEDED:
      return {
        ...INIT_STATE,
        ...action?.payload?.data?.state?.input?.program,
      };
    case programActionTypes.UPDATE_PROGRAM:
      return {
        ...state,
        ...action.payload,
      };
    case programActionTypes.UPDATE_DATA_QUALITY:
      return {
        ...state,
        dataQuality: action.payload,
      };
    case programActionTypes.UPDATE_INSURED_NAME:
      return {
        ...state,
        insured: action.payload,
      };
    case programActionTypes.UPDATE_MODEL:
      return {
        ...state,
        model: action.payload,
      };
    case programActionTypes.UPDATE_RISK_DESCRIPTION:
      return {
        ...state,
        riskDescription: action.payload,
      };
    case programActionTypes.UPDATE_INCEPTION:
      return {
        ...state,
        inception: strings.setEmptyToNull(action.payload),
      };
    case programActionTypes.UPDATE_EXPIRATION:
      return {
        ...state,
        expiration: strings.setEmptyToNull(action.payload),
      };
    case programActionTypes.UPDATE_PRO_RATA_ADJUSTMENT:
      return {
        ...state,
        proRataAdjustment: action.payload,
      };
    case programActionTypes.UPDATE_REFERENCE:
      return {
        ...state,
        reference: action.payload,
      };
    case programActionTypes.CLEAR_COMPANY_NAICS:
      return {
        ...state,
        companyNaics: undefined,
      };
    case programActionTypes.SET_COMPANY_NAICS:
      return {
        ...state,
        companyNaics: action.payload ?? undefined,
      };
    // Derived from the GL Pricing classifications. A NAICS the underwriter
    // chose wins and is never re-derived; only an empty field or an earlier
    // derived value is replaced. Submissions predating `naicsAuto` therefore
    // keep whatever was picked by hand.
    case programActionTypes.SET_DERIVED_NAICS:
      if (action.payload == null) {
        return state;
      }
      if (state.naics != null && !state.naicsAuto) {
        return state;
      }
      if (state.naics?.code === action.payload.code) {
        return state;
      }
      return {
        ...state,
        naics: action.payload,
        naicsAuto: true,
      };
    case searchActionTypes.SELECT_SEARCH_VALUE:
      switch (action.payload?.searchType) {
        case config.NAICS_SEEKER.key:
          return {
            ...state,
            naics: {
              code: action.payload.selectedValue._CODE,
              title: action.payload.selectedValue._TITLE,
            },
            // A hand-picked NAICS is an override — stop deriving it.
            naicsAuto: false,
          };
        case config.COMPANY_NAICS_SEEKER.key:
          return {
            ...state,
            companyNaics: {
              code: action.payload.selectedValue._CODE,
              title: action.payload.selectedValue._TITLE,
            },
          };
        case config.CATEGORY_CODE_SEEKER.key:
          return {
            ...state,
            categoryCode: {
              code: action.payload.selectedValue._CODE,
              title: action.payload.selectedValue._TITLE,
            },
          };
        default:
          return state;
      }
    case programActionTypes.UPDATE_WHETHER_IN_US:
      return {
        ...state,
        inUS: action.payload,
      };
    case programActionTypes.UPDATE_TURNOVER:
      return {
        ...state,
        turnoverCategory: action.payload,
      };
    case programActionTypes.UPDATE_INDUSTRY_CLASS:
      return {
        ...state,
        industryClass: action.payload,
      };
    case programActionTypes.UPDATE_BROKERAGE:
      return {
        ...state,
        brokerage: parseFloatOrNull(action.payload),
      };
    case programActionTypes.UPDATE_STATE_OF_FILING:
      return {
        ...state,
        stateOfFiling: action.payload,
      };
    case programActionTypes.UPDATE_REQUESTED_BROKERAGE:
      return {
        ...state,
        requestedBrokerage: parseFloatOrNull(action.payload),
      };
    case programActionTypes.UPDATE_OTHER_ACQUISITION_COSTS:
      return {
        ...state,
        otherAcquisitionCosts: parseFloatOrNull(action.payload),
      };
    case programActionTypes.UPDATE_PROFIT:
      return {
        ...state,
        profit: parseFloatOrNull(action.payload),
      };
    case programActionTypes.UPDATE_EXPENSES:
      return {
        ...state,
        expenses: parseFloatOrNull(action.payload),
      };
    case programActionTypes.UPDATE_INSURED_ADDRESS:
      return {
        ...state,
        insuredAddress: action.payload,
      };
    case programActionTypes.UPDATE_MAILING_ADDRESS:
      return {
        ...state,
        mailingAddress: action.payload,
      };
    case programActionTypes.UPDATE_INSURED_ADDRESS_COMPONENTS:
      return {
        ...state,
        insuredAddressComponents: action.payload,
      };
    case programActionTypes.UPDATE_CURRENCY:
      return {
        ...state,
        currency: action.payload,
      };
    case programActionTypes.UPDATE_REASSURED:
      return {
        ...state,
        reassured: action.payload === "" ? null : action.payload ?? null,
      };
    case programActionTypes.UPDATE_CESSION: {
      const normalise = (value) => (value === "" ? null : value ?? null);
      const updated = {
        ...state,
        ...Object.fromEntries(
          Object.entries(action.payload ?? {}).map(([key, value]) => [
            key,
            normalise(value),
          ])
        ),
      };
      // The insured name is the RRRe account name, assembled from these four
      // fields, so every change to them rewrites it rather than leaving a
      // stale hand-typed name behind.
      return {
        ...updated,
        insured: cession.formatAccountName(updated),
      };
    }
    case programActionTypes.UPDATE_PAYMENT_TERMS:
      return {
        ...state,
        paymentTerms: action.payload ?? null,
      };
    case programActionTypes.ADD_DUNS:
      return {
        ...state,
        [key]: [
          ...(state[key] || []).filter((item) => item !== action.payload),
          action.payload,
        ],
      };
    case programActionTypes.UPDATE_PROGRAM_NOTES:
      return {
        ...state,
        notes: action.payload,
      };
    case programActionTypes.UPDATE_RISK_SUMMARY:
      return {
        ...state,
        riskSummary: action.payload,
      };
    case programActionTypes.REMOVE_DUNS:
      return {
        ...state,
        [key]: (state[key] || []).filter((item) => item !== action.payload),
      };
    case programActionTypes.UPDATE_PRIOR_SUBMISSION:
      return {
        ...state,
        priorSubmission: action.payload,
      };
    case programActionTypes.UPDATE_SURPLUS_LINES_TAX_REPORTING:
      return {
        ...state,
        surplusLinesTaxReporting: {
          ...(state.surplusLinesTaxReporting ?? {}),
          [action.payload.field]: action.payload.value,
        },
      };
    case programActionTypes.SEED_SURPLUS_LINES_TAX_REPORTING:
      return {
        ...state,
        surplusLinesTaxReporting: {
          ...action.payload,
          ...(state.surplusLinesTaxReporting ?? {}),
        },
      };
    case programActionTypes.UPDATE_AUTO_EXPOSURE_RATING:
      return {
        ...state,
        autoExposureRating: {
          ...(state.autoExposureRating ?? {}),
          [action.payload.field]: action.payload.value,
        },
      };
    case programActionTypes.UPDATE_LOSS_RATING:
      return {
        ...state,
        lossRating: {
          ...(state.lossRating ?? {}),
          [action.payload.field]: action.payload.value,
        },
      };
    case programActionTypes.UPDATE_AUTO_LOSS_RATING:
      return {
        ...state,
        autoLossRating: {
          ...(state.autoLossRating ?? {}),
          [action.payload.field]: action.payload.value,
        },
      };
    case programActionTypes.UPDATE_EXPERIENCE_RATING:
      return {
        ...state,
        experienceRating: {
          ...(state.experienceRating ?? {}),
          [action.payload.field]: action.payload.value,
        },
      };
    case programActionTypes.UPDATE_XS_RATING:
      return {
        ...state,
        xsRating: {
          ...(state.xsRating ?? {}),
          [action.payload.field]: action.payload.value,
        },
      };
    case programActionTypes.UPDATE_RENEWAL_RATE_CHANGE: {
      const { section, field, value } = action.payload;
      const renewalRateChange = state.renewalRateChange ?? {};
      return {
        ...state,
        renewalRateChange: section
          ? {
              ...renewalRateChange,
              [section]: {
                ...(renewalRateChange[section] ?? {}),
                [field]: value,
              },
            }
          : { ...renewalRateChange, [field]: value },
      };
    }
    case programActionTypes.SET_AUTO_PRICING_RESULT:
      return { ...state, autoPricingResult: action.payload };
    case programActionTypes.SET_LOSS_RATING_RESULT:
      return { ...state, lossRatingResult: action.payload };
    case programActionTypes.SET_AUTO_LOSS_RATING_RESULT:
      return { ...state, autoLossRatingResult: action.payload };
    case programActionTypes.SET_EXPERIENCE_RATING_RESULT:
      return { ...state, experienceRatingResult: action.payload };
    case programActionTypes.SET_XS_RATING_RESULT:
      return { ...state, xsRatingResult: action.payload };
    case programActionTypes.SET_XS_RATING_AUTO_CALCULATE:
      return { ...state, xsRatingAutoCalculate: action.payload };
    case programActionTypes.UPDATE_LOSS_RATING_YEAR: {
      const { yearIndex, field, value } = action.payload;
      const currentYears = state.lossRating?.policyYears ?? [];
      const updatedYears = currentYears.map((y, i) =>
        i === yearIndex ? { ...y, [field]: value } : y
      );
      return {
        ...state,
        lossRating: {
          ...(state.lossRating ?? {}),
          policyYears: updatedYears,
        },
      };
    }
    default:
      return state;
  }
};

export default programReducer;
